/**
 * Credits Service
 * Handles credit management for users (pre-pay credits system)
 * Replaces subscription model with pay-per-appointment credits
 */

import { AuthManager } from '../utils/authManager';
import ProductionStorage from '../utils/productionStorage';
import { Platform } from 'react-native';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_CREDITS_API_URL || 'https://bookapt-user-credits.onrender.com';

export interface CreditPack {
  id: 'pack20' | 'pack40' | 'pack100';
  name: string;
  price: number;
  credits: number;
  pricePerCredit: number;
  description: string;
}

/** Tiered credit pack pricing — keep in sync with backend-user-credits.js CREDIT_PACKS */
export const CREDIT_PACK_CONFIG: Record<
  CreditPack['id'],
  Pick<CreditPack, 'credits' | 'price' | 'pricePerCredit'>
> = {
  pack20: { credits: 20, price: 19.99, pricePerCredit: 0.99 },
  pack40: { credits: 40, price: 31.99, pricePerCredit: 0.8 },
  pack100: { credits: 100, price: 59.99, pricePerCredit: 0.6 },
};

/** Rough avg $/credit for admin revenue estimates when purchase mix is unknown */
export const ESTIMATED_AVG_CREDIT_PRICE = 0.74;

export interface UserCredits {
  availableCredits: number;
  freeCreditsRemaining: number;
  purchasedCredits: number;
  hasUsedFreeCredits: boolean;
  autoReloadEnabled: boolean;
  autoReloadPack: 'pack20' | 'pack40' | 'pack100';
  autoReloadThreshold: number;
  lifetimeCreditsPurchased: number;
  lifetimeCreditsUsed: number;
  totalCredits: number;
}

export interface PurchaseResult {
  success: boolean;
  purchase?: {
    purchaseId: string;
    creditsAdded: number;
    newBalance: number;
    autoReloadTriggered: boolean;
  };
  error?: string;
}

export interface ConsumeCreditResult {
  success: boolean;
  credits?: {
    consumed: number;
    availableCredits: number;
    freeCreditsRemaining: number;
    purchasedCredits: number;
    autoReloadTriggered: boolean;
  };
  error?: string;
}

class CreditsService {
  private readonly CREDITS_STORAGE_KEY = 'user_credits';
  private readonly FREE_CREDITS = 5;

  /**
   * Get available credit packs
   * Falls back to default Stripe packages if API fails
   */
  async getCreditPacks(): Promise<CreditPack[]> {
    try {
      console.log('🔍 CreditsService: Fetching credit packs from API:', `${API_BASE_URL}/api/user/credits/packs`);
      
      const response = await fetch(`${API_BASE_URL}/api/user/credits/packs`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        console.warn(`⚠️ CreditsService: API returned status ${response.status}, using default packages`);
        return this.getDefaultCreditPacks();
      }
      
      const data = await response.json();
      console.log('🔍 CreditsService: API response:', data);

      if (data.success && data.packs && Array.isArray(data.packs) && data.packs.length > 0) {
        console.log(`✅ CreditsService: Loaded ${data.packs.length} credit packs from API`);
        return data.packs;
      }

      console.warn('⚠️ CreditsService: API response invalid, using default packages');
      return this.getDefaultCreditPacks();
    } catch (error: any) {
      console.error('❌ CreditsService: Error fetching credit packs from API:', error);
      console.log('📦 CreditsService: Falling back to default Stripe packages');
      return this.getDefaultCreditPacks();
    }
  }

  /**
   * Get default credit packs (fallback when API is unavailable)
   * These match the Stripe products configured in the backend
   */
  private getDefaultCreditPacks(): CreditPack[] {
    return [
      {
        id: 'pack20',
        name: '20 Appointment Credits',
        ...CREDIT_PACK_CONFIG.pack20,
        description: '20 credits · $0.99 per booking',
      },
      {
        id: 'pack40',
        name: '40 Appointment Credits',
        ...CREDIT_PACK_CONFIG.pack40,
        description: '40 credits · $0.80 per booking · Save 19%',
      },
      {
        id: 'pack100',
        name: '100 Appointment Credits',
        ...CREDIT_PACK_CONFIG.pack100,
        description: '100 credits · $0.60 per booking · Best Value!',
      },
    ];
  }

  /**
   * Clear cached credits (useful for debugging or forcing refresh)
   */
  async clearCreditsCache(): Promise<void> {
    try {
      await ProductionStorage.removeItem(this.CREDITS_STORAGE_KEY);
      console.log('✅ CreditsService: Cleared credits cache');
    } catch (error) {
      console.error('Error clearing credits cache:', error);
    }
  }

  /**
   * Get user's credit balance (Firestore first, then API, then local fallback)
   */
  async getUserCredits(userId: string, forceRefresh: boolean = false): Promise<UserCredits> {
    try {
      // Clear cache if force refresh requested
      if (forceRefresh) {
        await this.clearCreditsCache();
      }

      // Try to load from Firestore first (most up-to-date)
      let loadedCredits: UserCredits | null = null;
      let loadedFromFirestore = false;
      
      // Try Firestore for both web and mobile (same database, same collection)
      if (Platform.OS === 'web') {
        // Check if Firestore has been timing out - if so, skip it and use API directly
        const firestoreDisabled = localStorage.getItem('firestore_disabled_for_credits') === 'true';
        
        if (!firestoreDisabled) {
          try {
            const { doc, getDoc, collection, setDoc } = require('firebase/firestore');
            const { getWebDb } = await import('./unifiedAuthService');
            
            // Get Firestore from the shared web Firebase instance (auto-initializes if needed)
            const firestoreDb = getWebDb();
            // Validate that db is a valid Firestore instance
            if (firestoreDb && typeof firestoreDb === 'object') {
              try {
                // Read from userCredits collection (top-level, not subcollection)
                const creditsDocRef = doc(firestoreDb, 'userCredits', userId);
                
                // Shorter timeout (5 seconds) - if Firestore is slow, use API instead
                const timeoutPromise = new Promise<never>((_, reject) => 
                  setTimeout(() => reject(new Error('Firestore read timeout')), 5000)
                );
                
                console.log(`📊 CreditsService: Attempting Firestore read for user ${userId}...`);
                const creditsDoc = await Promise.race([
                  getDoc(creditsDocRef),
                  timeoutPromise
                ]);
                console.log(`📊 CreditsService: Firestore read completed`);
              if (creditsDoc.exists()) {
                const firestoreData = creditsDoc.data();
                
                // Convert Firestore data to UserCredits format
                const FREE_CREDITS = 5;
                const freeRemaining = firestoreData.hasUsedFreeCredits 
                  ? 0 
                  : (FREE_CREDITS - (firestoreData.freeCreditsUsed || 0));
                const purchasedCredits = firestoreData.purchasedCredits || 0;
                const availableCredits = freeRemaining + purchasedCredits;
                
                // Debug logging
                console.log('🔍 CreditsService: Firestore data conversion:', {
                  purchasedCredits: purchasedCredits,
                  freeRemaining: freeRemaining,
                  availableCredits: availableCredits,
                  hasUsedFreeCredits: firestoreData.hasUsedFreeCredits,
                  freeCreditsUsed: firestoreData.freeCreditsUsed,
                  lifetimeCreditsPurchased: firestoreData.lifetimeCreditsPurchased,
                });
                
                loadedCredits = {
                  availableCredits,
                  freeCreditsRemaining: freeRemaining,
                  purchasedCredits: purchasedCredits,
                  hasUsedFreeCredits: firestoreData.hasUsedFreeCredits || false,
                  autoReloadEnabled: firestoreData.autoReloadEnabled || false,
                  autoReloadPack: firestoreData.autoReloadPack || 'pack40',
                  autoReloadThreshold: firestoreData.autoReloadThreshold || 5,
                  lifetimeCreditsPurchased: firestoreData.lifetimeCreditsPurchased || 0,
                  lifetimeCreditsUsed: firestoreData.lifetimeCreditsUsed || 0,
                  totalCredits: FREE_CREDITS + (firestoreData.lifetimeCreditsPurchased || 0),
                };
                loadedFromFirestore = true;
                console.log(`✅ CreditsService: Loaded credits from Firestore - Available: ${availableCredits}`);
                
                // Sync to local storage
                await ProductionStorage.setItem(this.CREDITS_STORAGE_KEY, JSON.stringify(loadedCredits));
              } else {
                // New user - initialize with 5 free credits in userCredits collection
                console.log('🎁 CreditsService: New user detected, initializing with 5 free credits');
                const initialCredits: UserCredits = {
                  availableCredits: 5,
                  freeCreditsRemaining: 5,
                  purchasedCredits: 0,
                  hasUsedFreeCredits: false,
                  autoReloadEnabled: false,
                  autoReloadPack: 'pack40',
                  autoReloadThreshold: 5,
                  lifetimeCreditsPurchased: 0,
                  lifetimeCreditsUsed: 0,
                  totalCredits: 5,
                };
                
                // Save to Firestore in userCredits collection
                await setDoc(creditsDocRef, {
                  userId,
                  freeCreditsUsed: 0,
                  hasUsedFreeCredits: false,
                  purchasedCredits: 0,
                  lifetimeCreditsPurchased: 0,
                  lifetimeCreditsUsed: 0,
                  autoReloadEnabled: false,
                  autoReloadPack: 'pack40',
                  autoReloadThreshold: 5,
                  lastPurchaseDate: null,
                  createdAt: new Date(),
                  updatedAt: new Date(),
                });
                
                loadedCredits = initialCredits;
                loadedFromFirestore = true;
                console.log(`✅ CreditsService: Initialized new user with 5 free credits`);
                
                // Sync to local storage
                await ProductionStorage.setItem(this.CREDITS_STORAGE_KEY, JSON.stringify(loadedCredits));
              }
              } catch (docError: any) {
                // Handle doc() errors specifically
                if (docError.message?.includes('Expected first argument to doc()')) {
                  console.warn('⚠️  CreditsService: Invalid Firestore instance, skipping Firestore load');
                  console.warn('⚠️  CreditsService: Firestore db type:', typeof firestoreDb);
                  console.warn('⚠️  CreditsService: Firestore db value:', firestoreDb);
                  // Disable Firestore since it's invalid
                  localStorage.setItem('firestore_disabled_for_credits', 'true');
                  console.log('💡 CreditsService: Firestore disabled due to invalid instance. Using API as primary source.');
                } else if (docError.message?.includes('timeout')) {
                  console.warn('⚠️  CreditsService: Firestore read timeout after 5s, disabling Firestore and using API');
                  // Disable Firestore for future requests (user can clear this if needed)
                  localStorage.setItem('firestore_disabled_for_credits', 'true');
                  console.log('💡 CreditsService: Firestore disabled. Using API as primary source.');
                  console.log('💡 To re-enable Firestore, run: localStorage.removeItem("firestore_disabled_for_credits")');
                } else {
                  console.warn('⚠️  CreditsService: Firestore read error:', docError.message);
                }
                // Don't throw - fall through to API fallback
              }
            } else {
              console.warn('⚠️  CreditsService: Firebase webDb not available, skipping Firestore load');
            }
          } catch (firestoreError: any) {
            // Don't log full error if it's the doc() error to avoid console spam
            if (!firestoreError.message?.includes('Expected first argument to doc()')) {
              console.warn('⚠️  CreditsService: Could not load credits from Firestore, trying API:', firestoreError.message);
            }
          }
        } else {
          console.log('📊 CreditsService: Firestore disabled (timed out previously), using API directly');
        }
      } else {
        // Mobile: Read from Firestore using react-native-firebase
        // Fix #1: Check if Firestore has been timing out - if so, skip it and use API directly (like webapp)
        const firestoreDisabled = await ProductionStorage.getItem('firestore_disabled_for_credits') === 'true';
        
        if (!firestoreDisabled) {
          try {
            const { getMobileFirestore } = await import('./unifiedAuthService');
            const mobileFirestoreFn = getMobileFirestore();
            
            if (mobileFirestoreFn) {
              try {
                console.log(`📊 CreditsService (mobile): Attempting Firestore read for user ${userId}...`);
                
                const mobileFirestore = mobileFirestoreFn();
                
                // Add timeout (5 seconds) - if Firestore is slow, use API instead
                const timeoutPromise = new Promise<never>((_, reject) => 
                  setTimeout(() => reject(new Error('Firestore read timeout')), 5000)
                );
                
                const creditsDoc = await Promise.race([
                  mobileFirestore.collection('userCredits').doc(userId).get(),
                  timeoutPromise
                ]);
                
                console.log(`📊 CreditsService (mobile): Firestore read completed`);
              
              if (creditsDoc.exists) {
                const firestoreData = creditsDoc.data();
                
                // Convert Firestore data to UserCredits format (same as web)
                const FREE_CREDITS = 5;
                const freeRemaining = firestoreData?.hasUsedFreeCredits 
                  ? 0 
                  : (FREE_CREDITS - (firestoreData?.freeCreditsUsed || 0));
                const purchasedCredits = firestoreData?.purchasedCredits || 0;
                const availableCredits = freeRemaining + purchasedCredits;
                
                // Debug logging
                console.log('🔍 CreditsService (mobile): Firestore data conversion:', {
                  purchasedCredits: purchasedCredits,
                  freeRemaining: freeRemaining,
                  availableCredits: availableCredits,
                  hasUsedFreeCredits: firestoreData?.hasUsedFreeCredits,
                  freeCreditsUsed: firestoreData?.freeCreditsUsed,
                  lifetimeCreditsPurchased: firestoreData?.lifetimeCreditsPurchased,
                });
                
                loadedCredits = {
                  availableCredits,
                  freeCreditsRemaining: freeRemaining,
                  purchasedCredits: purchasedCredits,
                  hasUsedFreeCredits: firestoreData?.hasUsedFreeCredits || false,
                  autoReloadEnabled: firestoreData?.autoReloadEnabled || false,
                  autoReloadPack: firestoreData?.autoReloadPack || 'pack40',
                  autoReloadThreshold: firestoreData?.autoReloadThreshold || 5,
                  lifetimeCreditsPurchased: firestoreData?.lifetimeCreditsPurchased || 0,
                  lifetimeCreditsUsed: firestoreData?.lifetimeCreditsUsed || 0,
                  totalCredits: FREE_CREDITS + (firestoreData?.lifetimeCreditsPurchased || 0),
                };
                loadedFromFirestore = true;
                console.log(`✅ CreditsService (mobile): Loaded credits from Firestore - Available: ${availableCredits}`);
                
                // Sync to local storage
                await ProductionStorage.setItem(this.CREDITS_STORAGE_KEY, JSON.stringify(loadedCredits));
              } else {
                // New user - initialize with 5 free credits
                console.log('🎁 CreditsService (mobile): New user detected, initializing with 5 free credits');
                const initialCredits: UserCredits = {
                  availableCredits: 5,
                  freeCreditsRemaining: 5,
                  purchasedCredits: 0,
                  hasUsedFreeCredits: false,
                  autoReloadEnabled: false,
                  autoReloadPack: 'pack40',
                  autoReloadThreshold: 5,
                  lifetimeCreditsPurchased: 0,
                  lifetimeCreditsUsed: 0,
                  totalCredits: 5,
                };
                
                // Save to Firestore
                await mobileFirestore.collection('userCredits').doc(userId).set({
                  userId,
                  freeCreditsUsed: 0,
                  hasUsedFreeCredits: false,
                  purchasedCredits: 0,
                  lifetimeCreditsPurchased: 0,
                  lifetimeCreditsUsed: 0,
                  autoReloadEnabled: false,
                  autoReloadPack: 'pack40',
                  autoReloadThreshold: 5,
                  lastPurchaseDate: null,
                  createdAt: new Date(),
                  updatedAt: new Date(),
                });
                
                loadedCredits = initialCredits;
                loadedFromFirestore = true;
                console.log(`✅ CreditsService (mobile): Initialized new user with 5 free credits`);
                
                // Sync to local storage
                await ProductionStorage.setItem(this.CREDITS_STORAGE_KEY, JSON.stringify(loadedCredits));
              }
            } catch (firestoreError: any) {
              if (firestoreError.message?.includes('timeout')) {
                console.warn('⚠️ CreditsService (mobile): Firestore read timeout after 5s, disabling Firestore and using API');
                // Fix #1: Set disable flag on timeout (like webapp)
                await ProductionStorage.setItem('firestore_disabled_for_credits', 'true');
                console.log('💡 CreditsService (mobile): Firestore disabled. Using API as primary source.');
                console.log('💡 To re-enable Firestore, clear: firestore_disabled_for_credits from storage');
              } else {
                console.warn('⚠️ CreditsService (mobile): Firestore read error:', firestoreError.message);
                console.warn('⚠️ CreditsService (mobile): Error stack:', firestoreError.stack);
              }
              // Don't throw - fall through to API fallback
            }
          } else {
            console.warn('⚠️ CreditsService (mobile): mobileFirestore not available, using API fallback');
            console.warn('⚠️ CreditsService (mobile): getMobileFirestore() returned null - Firebase may not be initialized');
          }
        } catch (error: any) {
          console.warn('⚠️ CreditsService (mobile): Could not load credits from Firestore, trying API:', error.message);
          console.warn('⚠️ CreditsService (mobile): Import error stack:', error.stack);
        }
        } else {
          console.log('📊 CreditsService (mobile): Firestore disabled (timed out previously), using API directly');
        }
      }
      
      // Fallback to API if Firestore didn't work
      if (!loadedFromFirestore) {
        try {
          console.log('📡 CreditsService: Firestore failed, trying API fallback...');
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout for API
          
          try {
            const response = await fetch(`${API_BASE_URL}/api/user/credits/balance/${userId}`, {
              signal: controller.signal,
            });
            clearTimeout(timeoutId);
            
            if (response.ok) {
              const data = await response.json();
              if (data.success && data.credits) {
                loadedCredits = data.credits;
                console.log(`✅ CreditsService: Loaded credits from API - Available: ${loadedCredits.availableCredits}`);
                console.log('🔍 CreditsService: API credits details:', {
                  availableCredits: loadedCredits.availableCredits,
                  purchasedCredits: loadedCredits.purchasedCredits,
                  freeCreditsRemaining: loadedCredits.freeCreditsRemaining,
                  lifetimeCreditsPurchased: loadedCredits.lifetimeCreditsPurchased,
                });
                
                // Cache credits locally
                await ProductionStorage.setItem(this.CREDITS_STORAGE_KEY, JSON.stringify(loadedCredits));
                
                // Also save to Firestore (async, don't wait) - WEB ONLY (unchanged)
                if (Platform.OS === 'web') {
                  this.saveCreditsToFirestore(userId, loadedCredits).catch(err => {
                    console.warn('⚠️ CreditsService: Could not save to Firestore (non-critical):', err);
                  });
                } else {
                  // Fix #2: Mobile - sync to Firestore after API fallback (mobile-only addition)
                  this.saveCreditsToFirestore(userId, loadedCredits).catch(err => {
                    console.warn('⚠️ CreditsService (mobile): Could not save to Firestore (non-critical):', err);
                  });
                }
                
                return loadedCredits;
              } else {
                console.warn('⚠️ CreditsService: API returned unsuccessful response:', data);
              }
            } else {
              console.warn(`⚠️ CreditsService: API returned status ${response.status}`);
            }
          } catch (fetchError: any) {
            clearTimeout(timeoutId);
            if (fetchError.name === 'AbortError') {
              console.warn('⚠️ CreditsService: API request timed out');
            } else {
              throw fetchError;
            }
          }
        } catch (apiError: any) {
          console.warn('⚠️ CreditsService: Could not load credits from API:', apiError.message);
        }
      }
      
      // Final fallback to local storage
      if (loadedCredits) {
        return loadedCredits;
      }
      
      // Last resort: try local storage
      const localCredits = await ProductionStorage.getItem(this.CREDITS_STORAGE_KEY);
      if (localCredits) {
        loadedCredits = JSON.parse(localCredits);
        console.log(`📋 CreditsService: Loaded credits from local storage`);
        return loadedCredits;
      }

      // Final fallback: Initialize new user with 5 free credits
      console.log('🎁 CreditsService: No credits found anywhere, initializing new user with 5 free credits');
      const initialCredits: UserCredits = {
        availableCredits: 5,
        freeCreditsRemaining: 5,
        purchasedCredits: 0,
        hasUsedFreeCredits: false,
        autoReloadEnabled: false,
        autoReloadPack: 'pack40',
        autoReloadThreshold: 5,
        lifetimeCreditsPurchased: 0,
        lifetimeCreditsUsed: 0,
        totalCredits: 5,
      };
      
      // Save to local storage
      await ProductionStorage.setItem(this.CREDITS_STORAGE_KEY, JSON.stringify(initialCredits));
      
      // Try to save to Firestore (async, don't wait) - WEB ONLY (unchanged)
      if (Platform.OS === 'web') {
        this.saveCreditsToFirestore(userId, initialCredits).catch(err => {
          console.warn('⚠️ Could not save initial credits to Firestore:', err);
        });
      } else {
        // Fix #2: Mobile - sync to Firestore for initial credits (mobile-only addition)
        this.saveCreditsToFirestore(userId, initialCredits).catch(err => {
          console.warn('⚠️ CreditsService (mobile): Could not save initial credits to Firestore:', err);
        });
      }
      
      console.log(`✅ CreditsService: Initialized new user with 5 free credits (fallback)`);
      return initialCredits;
    } catch (error) {
      console.error('Error getting user credits:', error);
      throw error;
    }
  }
  
  // Save credits to Firestore (works for both web and mobile)
  private async saveCreditsToFirestore(userId: string, credits: UserCredits): Promise<void> {
    try {
      if (Platform.OS === 'web') {
        const { getWebDb, initializeFirebase } = await import('./unifiedAuthService');
        
        initializeFirebase();
        
        const db = getWebDb();
        if (!db) {
          console.warn('⚠️ CreditsService: Firestore db not available, skipping save');
          return;
        }
        
        // Use same firebase/firestore module instance as unifiedAuthService (avoids doc() mismatch)
        const { doc, setDoc } = require('firebase/firestore');
        
        try {
          const creditsDocRef = doc(db, 'userCredits', userId);
          await setDoc(creditsDocRef, {
            userId,
            freeCreditsUsed: credits.freeCreditsRemaining ? 0 : 5,
            hasUsedFreeCredits: credits.hasUsedFreeCredits || false,
          purchasedCredits: credits.purchasedCredits || 0,
          lifetimeCreditsPurchased: credits.lifetimeCreditsPurchased || 0,
          lifetimeCreditsUsed: credits.lifetimeCreditsUsed || 0,
          autoReloadEnabled: credits.autoReloadEnabled || false,
          autoReloadPack: credits.autoReloadPack || 'pack40',
          autoReloadThreshold: credits.autoReloadThreshold || 5,
          lastPurchaseDate: null,
          updatedAt: new Date(),
        }, { merge: true });
        console.log(`✅ CreditsService (web): Credits saved to Firestore`);
      } catch (docError: any) {
        // If doc() fails, log but don't throw - this is a sync operation
        if (docError.message?.includes('Expected first argument to doc()')) {
          console.warn('⚠️ CreditsService: Invalid Firestore instance format, skipping save');
        } else {
          console.warn('⚠️ CreditsService: Could not save to Firestore (non-critical):', docError.message);
        }
      }
      } else {
        // Mobile: Save to Firestore using react-native-firebase
        try {
          const { getMobileFirestore } = await import('./unifiedAuthService');
          const mobileFirestoreFn = getMobileFirestore();
          
          if (mobileFirestoreFn) {
            const mobileFirestore = mobileFirestoreFn();
            await mobileFirestore.collection('userCredits').doc(userId).set({
              userId,
              freeCreditsUsed: credits.freeCreditsRemaining ? 0 : 5,
              hasUsedFreeCredits: credits.hasUsedFreeCredits || false,
              purchasedCredits: credits.purchasedCredits || 0,
              lifetimeCreditsPurchased: credits.lifetimeCreditsPurchased || 0,
              lifetimeCreditsUsed: credits.lifetimeCreditsUsed || 0,
              autoReloadEnabled: credits.autoReloadEnabled || false,
              autoReloadPack: credits.autoReloadPack || 'pack40',
              autoReloadThreshold: credits.autoReloadThreshold || 5,
              lastPurchaseDate: null,
              updatedAt: new Date(),
            }, { merge: true });
            console.log(`✅ CreditsService (mobile): Credits saved to Firestore`);
          } else {
            console.warn('⚠️ CreditsService (mobile): mobileFirestore not available, skipping save');
          }
        } catch (mobileError: any) {
          console.warn('⚠️ CreditsService (mobile): Could not save to Firestore (non-critical):', mobileError.message);
        }
      }
    } catch (error: any) {
      console.warn('⚠️ CreditsService: Error saving credits to Firestore (non-critical):', error?.message || error);
      // Don't throw - this is a sync operation and shouldn't block the main flow
    }
  }

  /**
   * Check if user has enough credits to book an appointment
   */
  async canBookAppointment(userId: string): Promise<boolean> {
    try {
      const credits = await this.getUserCredits(userId);
      return credits.availableCredits > 0;
    } catch (error) {
      console.error('Error checking if user can book:', error);
      return true; // Fail open
    }
  }

  /**
   * Create a payment intent for credit purchase (Web - Stripe)
   */
  async createCreditPurchase(
    packId: 'pack20' | 'pack40' | 'pack100',
    userId: string,
    userEmail: string,
    userName?: string
  ): Promise<{ clientSecret: string; paymentIntentId: string; packId: string; credits: number; amount: number }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/credits/purchase/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          packId,
          userId,
          userEmail,
          userName
        })
      });

      const data = await response.json();

      if (data.success) {
        return {
          clientSecret: data.clientSecret,
          paymentIntentId: data.paymentIntentId,
          packId: data.packId,
          credits: data.credits,
          amount: data.amount
        };
      }

      throw new Error(data.error || 'Failed to create credit purchase');
    } catch (error) {
      console.error('Error creating credit purchase:', error);
      throw error;
    }
  }

  /**
   * Confirm credit purchase after payment succeeds (Web - Stripe)
   */
  async confirmCreditPurchase(paymentIntentId: string, userId: string): Promise<PurchaseResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/credits/purchase/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          paymentIntentId,
          userId
        })
      });

      const data = await response.json();

      if (data.success) {
        // Refresh credits from server
        await this.getUserCredits(userId);
        return data;
      }

      return {
        success: false,
        error: data.error || 'Failed to confirm purchase'
      };
    } catch (error) {
      console.error('Error confirming credit purchase:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to confirm purchase'
      };
    }
  }

  /**
   * Handle wallet payment (PayPal, Apple Pay, Google Pay)
   */
  async processWalletPurchase(
    userId: string,
    packId: string,
    transactionId: string,
    paymentMethod: string = 'wallet'
  ): Promise<PurchaseResult> {
    try {
      console.log('Calling wallet purchase API:', {
        url: `${API_BASE_URL}/api/user/credits/wallet-purchase`,
        userId,
        packId,
        transactionId,
        paymentMethod
      });

      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      try {
        const response = await fetch(`${API_BASE_URL}/api/user/credits/wallet-purchase`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId,
            packId,
            transactionId,
            paymentMethod
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          let errorText = '';
          try {
            errorText = await response.text();
            // Try to parse as JSON if possible
            try {
              const errorJson = JSON.parse(errorText);
              errorText = errorJson.error || errorText;
            } catch {
              // Not JSON, use as-is
            }
          } catch {
            errorText = `HTTP ${response.status}`;
          }
          
          console.error('Wallet purchase API error:', response.status, errorText);
          return {
            success: false,
            error: errorText || `Server error: ${response.status}`
          };
        }

        const data = await response.json();

        if (data.success) {
          // Refresh credits from server
          await this.getUserCredits(userId);
          return data;
        }

        return {
          success: false,
          error: data.error || 'Failed to process wallet purchase'
        };
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          console.error('Wallet purchase API timeout');
          return {
            success: false,
            error: 'Request timed out. Please check your connection and try again.'
          };
        }
        throw fetchError;
      }
    } catch (error) {
      console.error('Error processing wallet purchase:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to process wallet purchase'
      };
    }
  }

  /**
   * Handle mobile in-app purchase (iOS/Android)
   */
  async processMobilePurchase(
    userId: string,
    productId: string,
    transactionId: string,
    platform: 'ios' | 'android'
  ): Promise<PurchaseResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/credits/mobile-purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          productId,
          transactionId,
          platform
        })
      });

      const data = await response.json();

      if (data.success) {
        // Refresh credits from server
        await this.getUserCredits(userId);
        return data;
      }

      return {
        success: false,
        error: data.error || 'Failed to process mobile purchase'
      };
    } catch (error) {
      console.error('Error processing mobile purchase:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to process mobile purchase'
      };
    }
  }

  /**
   * Consume 1 credit when user books an appointment
   */
  async consumeCredit(userId: string, appointmentId: string): Promise<ConsumeCreditResult> {
    try {
      // Try API first
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/credits/consume`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId,
            appointmentId
          })
        });

        const data = await response.json();

        if (data.success) {
          // Refresh credits from server
          await this.getUserCredits(userId);
          
          // Check if auto-reload should trigger
          if (data.credits.autoReloadTriggered) {
            // Handle auto-reload (would trigger purchase flow)
            console.log('Auto-reload triggered - credits below threshold');
          }

          return data;
        }

        return {
          success: false,
          error: data.error || 'Insufficient credits'
        };
      } catch (apiError) {
        console.warn('⚠️ CreditsService: API unavailable, using local storage fallback:', apiError);
        
        // Fallback to local storage
        const currentCredits = await this.getUserCredits(userId);
        
        if (currentCredits.availableCredits < 1) {
          return {
            success: false,
            error: 'Insufficient credits'
          };
        }
        
        // Deduct 1 credit locally
        const updatedCredits: UserCredits = {
          ...currentCredits,
          availableCredits: Math.max(0, currentCredits.availableCredits - 1),
          lifetimeCreditsUsed: (currentCredits.lifetimeCreditsUsed || 0) + 1,
        };
        
        // Save to local storage
        await ProductionStorage.setItem(this.CREDITS_STORAGE_KEY, JSON.stringify(updatedCredits));
        
        // Also save to Firestore if available
        if (Platform.OS === 'web') {
          try {
            await this.saveCreditsToFirestore(userId, updatedCredits);
          } catch (firestoreError) {
            console.warn('⚠️ CreditsService: Could not save to Firestore:', firestoreError);
          }
        }
        
        console.log('✅ CreditsService: Credit deducted locally. New balance:', updatedCredits.availableCredits);
        
        return {
          success: true,
          credits: {
            consumed: 1,
            availableCredits: updatedCredits.availableCredits,
            freeCreditsRemaining: updatedCredits.freeCreditsRemaining,
            purchasedCredits: updatedCredits.purchasedCredits,
            autoReloadTriggered: false,
          }
        };
      }
    } catch (error) {
      console.error('Error consuming credit:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to consume credit'
      };
    }
  }

  /**
   * Update auto-reload settings
   */
  async updateAutoReloadSettings(
    userId: string,
    settings: {
      enabled?: boolean;
      packId?: 'pack20' | 'pack40' | 'pack100';
      threshold?: number;
    }
  ): Promise<{ success: boolean; settings?: any; error?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/credits/auto-reload/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          ...settings
        })
      });

      const data = await response.json();

      if (data.success) {
        // Refresh credits to get updated settings
        await this.getUserCredits(userId);
        return data;
      }

      return {
        success: false,
        error: data.error || 'Failed to update auto-reload settings'
      };
    } catch (error) {
      console.error('Error updating auto-reload settings:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update auto-reload settings'
      };
    }
  }

  /**
   * Get purchase history
   */
  async getPurchaseHistory(userId: string): Promise<any[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/credits/history/${userId}`);
      const data = await response.json();

      if (data.success) {
        return data.purchases || [];
      }

      return [];
    } catch (error) {
      console.error('Error getting purchase history:', error);
      return [];
    }
  }
}

export const creditsService = new CreditsService();
export default creditsService;

